/**
 * Headless Voice Stack attach. Kit Voice tab sets this selector.
 * Do not add a second button. Do not edit kit.
 */

import { HOTTIE_VOICE_ATTACH } from '@/kit/hottie';

export { HOTTIE_VOICE_ATTACH };

/** Existing kit rail/dock Orb button. Brand composes this node into the Voice well. */
export const HOTTIE_ORB_SLOT = '.hottr-orb-slot';

export type QueryRoot = Pick<ParentNode, 'querySelector'>;

export function isHottieVoiceAttached(root: QueryRoot): boolean {
  return Boolean(root.querySelector(HOTTIE_VOICE_ATTACH));
}

export function voiceAttachTransition(
  wasAttached: boolean,
  isAttached: boolean,
): 'start' | 'stop' | null {
  if (wasAttached === isAttached) return null;
  return isAttached ? 'start' : 'stop';
}

/**
 * Watch the locked kit selector. `onChange(true)` when Voice is open,
 * `onChange(false)` when leaving Voice or closing the sheet.
 */
export function watchHottieVoiceAttach(
  onChange: (attached: boolean) => void,
  opts?: { root?: QueryRoot; observe?: Node },
): () => void {
  const root =
    opts?.root ??
    (typeof document !== 'undefined' ? document : { querySelector: () => null });
  const observe =
    opts?.observe ?? (typeof document !== 'undefined' ? document.body : null);

  let attached = false;
  const sync = () => {
    const next = isHottieVoiceAttached(root);
    const action = voiceAttachTransition(attached, next);
    attached = next;
    if (action === 'start') onChange(true);
    if (action === 'stop') onChange(false);
  };

  sync();

  if (!observe || typeof MutationObserver === 'undefined') {
    return () => {
      if (attached) onChange(false);
    };
  }

  const observer = new MutationObserver(sync);
  observer.observe(observe, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['data-open', 'data-mode', 'class'],
  });

  return () => {
    observer.disconnect();
    if (attached) onChange(false);
  };
}

type TabLike = {
  nodeType?: number;
  textContent?: string | null;
  parentElement?: TabLike | null;
  closest?: (sel: string) => TabLike | null;
};

const VOICE_TAB_SEL = '.hottr-hottie__mode, [role="tab"]';

export type VoiceTabEvent = {
  type?: string;
  target?: EventTarget | null;
  key?: string;
  composedPath?: () => EventTarget[];
};

function hasVoiceWord(text: string | null | undefined): boolean {
  return /\bvoice\b/i.test((text ?? '').replace(/\s+/g, ' ').trim());
}

/** Text nodes have no closest(). Walk to the element. */
function elementFromTarget(target: EventTarget | null | undefined): TabLike | null {
  if (!target || typeof target !== 'object') return null;
  const node = target as TabLike;
  if (node.nodeType === 3) return node.parentElement ?? null;
  return node;
}

function tabFromNode(target: EventTarget | null | undefined): TabLike | null {
  const el = elementFromTarget(target);
  if (!el) return null;
  return el.closest?.(VOICE_TAB_SEL) ?? null;
}

/**
 * Resolve the kit Voice tab from composedPath, then target / parentElement.
 * Label is the word "voice" (case-insensitive). Already-selected still matches.
 */
export function resolveVoiceTab(event: VoiceTabEvent): TabLike | null {
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const nodes = path.length > 0 ? path : [event.target ?? null];
  for (const node of nodes) {
    const tab = tabFromNode(node);
    if (tab && hasVoiceWord(tab.textContent)) return tab;
  }
  return null;
}

/** Any Voice tab — including already selected. Chat does not match. */
export function isVoiceTabActivating(
  target: EventTarget | null,
  event?: VoiceTabEvent,
): boolean {
  return Boolean(resolveVoiceTab(event ?? { target }));
}

export function isVoiceTabArmEvent(event: VoiceTabEvent): boolean {
  if (event.type === 'keydown') {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return false;
  } else if (event.type !== 'pointerdown' && event.type !== 'click') {
    return false;
  }
  return Boolean(resolveVoiceTab(event));
}

/**
 * Arm start on the existing Voice tab tap / click / Enter / Space, in the
 * same user gesture. Capture phase so getUserMedia runs on that turn.
 */
export function watchVoiceTabGesture(
  onArm: () => void,
  root: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null = typeof document !== 'undefined' ? document : null,
): () => void {
  if (!root) return () => {};

  const onEvent = (event: Event) => {
    if (!isVoiceTabArmEvent(event as KeyboardEvent)) return;
    onArm();
  };

  root.addEventListener('pointerdown', onEvent, true);
  root.addEventListener('click', onEvent, true);
  root.addEventListener('keydown', onEvent, true);
  return () => {
    root.removeEventListener('pointerdown', onEvent, true);
    root.removeEventListener('click', onEvent, true);
    root.removeEventListener('keydown', onEvent, true);
  };
}

type ClosestLike = {
  closest?: (sel: string) => unknown;
};

/** Target is the existing `.hottr-orb-slot` or a child of it. */
export function isOrbSlotTarget(target: EventTarget | null): boolean {
  if (!target || typeof target !== 'object') return false;
  return Boolean((target as ClosestLike).closest?.(HOTTIE_ORB_SLOT));
}

export function isVoiceOrbArmEvent(
  event: Pick<Event, 'type' | 'target'> & { key?: string },
  voiceAttached: boolean,
): boolean {
  if (!voiceAttached || !isOrbSlotTarget(event.target)) return false;
  if (event.type === 'keydown') {
    return event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
  }
  return event.type === 'pointerdown';
}

export function isVoiceOrbDismissEvent(
  event: Pick<Event, 'type' | 'target'>,
  voiceAttached: boolean,
): boolean {
  if (!voiceAttached || !isOrbSlotTarget(event.target)) return false;
  return event.type === 'pointerup' || event.type === 'click';
}

/**
 * While Voice is attached, the composed rail Orb is still `.hottr-orb-slot`.
 * The well is pointer-events: none, so a tap hits the slot and OrbRail
 * dismisses the sheet. Capture-phase: arm start on pointerdown / Enter /
 * Space; swallow pointerup / click so dismiss does not run. Close, scrim,
 * and Escape are not on the slot — they still close.
 */
export function watchVoiceOrbSlotGesture(
  onArm: () => void,
  opts?: {
    root?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null;
    queryRoot?: QueryRoot;
  },
): () => void {
  const root =
    opts?.root ?? (typeof document !== 'undefined' ? document : null);
  const queryRoot =
    opts?.queryRoot ??
    (typeof document !== 'undefined' ? document : { querySelector: () => null });
  if (!root) return () => {};

  const voiceOpen = () => isHottieVoiceAttached(queryRoot);

  const onArmEvent = (event: Event) => {
    if (!isVoiceOrbArmEvent(event as KeyboardEvent, voiceOpen())) return;
    onArm();
  };

  const onDismissEvent = (event: Event) => {
    if (!isVoiceOrbDismissEvent(event, voiceOpen())) return;
    event.stopPropagation();
    event.preventDefault();
  };

  root.addEventListener('pointerdown', onArmEvent, true);
  root.addEventListener('keydown', onArmEvent, true);
  root.addEventListener('pointerup', onDismissEvent, true);
  root.addEventListener('click', onDismissEvent, true);
  return () => {
    root.removeEventListener('pointerdown', onArmEvent, true);
    root.removeEventListener('keydown', onArmEvent, true);
    root.removeEventListener('pointerup', onDismissEvent, true);
    root.removeEventListener('click', onDismissEvent, true);
  };
}
